import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { BankAccountManager } from "@/components/settings/BankAccountManager";
import { Wallet, ArrowRight, CheckCircle2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";

interface PaymentOnboardingProps {
  onComplete: () => void;
}

export function PaymentOnboarding({ onComplete }: PaymentOnboardingProps) {
  const [step, setStep] = useState(1);
  const { tenantId } = useTenant();
  const [hasAccounts, setHasAccounts] = useState(false);

  // Verificar se há contas cadastradas
  const { data: bankAccounts = [] } = useQuery({
    queryKey: ["bank_accounts", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id")
        .eq("tenant_id", tenantId)
        .eq("status", "ativo");

      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  useEffect(() => {
    setHasAccounts(bankAccounts.length > 0);
  }, [bankAccounts]);

  const handleComplete = () => {
    localStorage.setItem("payment-onboarding-completed", "true");
    onComplete();
  };

  return (
    <div className="space-y-3">
      {/* Barra de progresso compacta */}
      <div className="flex items-center gap-2">
        <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? "bg-primary" : "bg-muted"}`} />
        <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? "bg-primary" : "bg-muted"}`} />
      </div>

      {/* Passo 1: Explicação compacta */}
      {step === 1 && (
        <div className="border border-primary/50 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Wallet className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Configure contas para registrar recebimentos de pagamentos</h3>
              <p className="text-xs text-muted-foreground">Passo 1 de 2</p>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground">
            Para registrar pagamentos, configure <strong>contas bancárias</strong> (Caixa, Nu Bank, etc.) e associe <strong>métodos</strong> (PIX, Dinheiro, Cartão).
          </p>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-muted/50 py-2 px-4 rounded text-xs space-y-1">
              <p className="font-medium">Exemplos de contas:</p>
              <ul className="text-muted-foreground space-y-0.5 ml-3 pl-2 list-disc">
                <li>Caixa Físico</li>
                <li>Nu Bank</li>
                <li>Mercado Pago</li>
              </ul>
            </div>
            <div className="bg-muted/50 py-2 px-4 rounded text-xs space-y-1">
              <p className="font-medium">Métodos disponíveis:</p>
              <ul className="text-muted-foreground space-y-0.5 ml-3 pl-2 list-disc">
                <li>PIX</li>
                <li>Dinheiro</li>
                <li>Cartões</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <Button type="button" onClick={() => setStep(2)} size="sm" className="gap-1">
              Cadastrar contas
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Passo 2: CRUD completo */}
      {step === 2 && (
        <div className="border border-primary/50 rounded-lg p-3 space-y-3">
          <BankAccountManager stacked showHeadings={false} onAccountCreated={() => setHasAccounts(true)} />

          <div className="flex justify-between pt-2 border-t mt-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setStep(1)}>
              Voltar
            </Button>
            <Button 
              type="button"
              onClick={handleComplete} 
              disabled={!hasAccounts}
              size="sm"
              className="gap-1"
            >
              Finalizar configuração
              <CheckCircle2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
